import React, { useContext, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { ConfigContext } from '../Context/ConfigContext';
import axios from 'axios';
import * as Yup from "yup";
import { classNames } from 'primereact/utils';
import { Divider } from 'primereact/divider';
import { Avatar } from 'primereact/avatar';
import { motion } from 'framer-motion';

const validationSchema = Yup.object({
    username: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
});

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const { apiUrl, Login, set_routes } = useContext(ConfigContext);
    const [showPassword, setShowPassword] = useState(false);
    const toast = useRef(null);

    const validate = () => {
        try {
            validationSchema.validateSync({ username, password }, { abortEarly: false });
            setErrors({});
            return true;
        } catch (err) {
            const newErrors = {};
            err.inner.forEach((error) => {
                newErrors[error.path] = error.message;
            });
            setErrors(newErrors);
            return false;
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!validate()) {
            setLoading(false);
            return;
        }

        const body = { login_id: username, login_password: password };

        try {
            const response = await axios.post(`${apiUrl}/User/Login`, body);
            const { success, data, message, permissions } = response.data;

            if (success) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: message, life: 3000 });
                Login(data[0]);
                set_routes(permissions);
                localStorage.setItem('routes', JSON.stringify(permissions));

                if (data[0]?.user_role === 'qr') {
                    setTimeout(() => {
                        window.location.href = "#/Qr";
                    }, 500);
                    return;
                }

                if (data[0]?.user_role === 'promo') {
                    setTimeout(() => {
                        window.location.href = "#/Promo";
                    }, 500);
                    return;
                }

                if (permissions.includes('Dashboard')) {
                    setTimeout(() => {
                        window.location.href = "#/";
                    }, 500);
                } else {
                    setTimeout(() => {
                        window.location.href = "#/Settings";
                    }, 500);
                }
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
            }
        } catch (error) {
            console.error("Login error:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'An error occurred. Please try again.', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <motion.div
                className="d-flex justify-content-center align-items-center vh-100"
                style={{ background: '#f7e6c4' }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
            >
                <motion.div
                    className="rounded"
                    style={{ width: '400px' }}
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <Card className="p-3 shadow-lg rounded" style={{ borderRadius: '15px', background: '#fff8e1' }}>
                        <div className="text-center">
                            <Avatar icon="pi pi-shop" className="py-3" size="xlarge" style={{ backgroundColor: '#ffb74d', color: 'white' }} />
                            <h4 className="mt-2" style={{ color: '#6d4c41' }}>Inventory Login</h4>
                            <p className="text-muted">Enter your credentials to continue</p>
                        </div>
                        <Divider />
                        <form onSubmit={handleLogin}>
                            <div className="mb-3">
                                <i className="pi pi-user me-2" />
                                <label htmlFor="username" className="fw-bold">Username</label>
                                <InputText
                                    id="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className={classNames('w-100', { 'p-invalid': errors.username })}
                                    placeholder="Enter username"
                                />
                                {errors.username && <small className="p-error">{errors.username}</small>}
                            </div>

                            <div className="mb-3">
                                <i className="pi pi-lock me-2" />
                                <label htmlFor="password" className="fw-bold">Password</label>
                                <div className="p-inputgroup">
                                    <InputText
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className={classNames('w-100', { 'p-invalid': errors.password })}
                                        placeholder="Enter password"
                                    />
                                    <motion.span
                                        className="p-inputgroup-addon"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{ cursor: 'pointer' }}
                                        whileTap={{ scale: 0.9 }}
                                    >
                                        <i className={showPassword ? "pi pi-eye-slash" : "pi pi-eye"} />
                                    </motion.span>
                                </div>
                                {errors.password && <small className="p-error">{errors.password}</small>}
                            </div>

                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Button
                                    type="submit"
                                    label={loading ? "Logging in..." : "Login"}
                                    icon={loading ? "pi pi-spin pi-spinner" : "pi pi-sign-in"}
                                    className="w-100 p-button-raised p-button-rounded"
                                    style={{ backgroundColor: '#ff7043', border: 'none' }}
                                    disabled={loading}
                                />
                            </motion.div>
                        </form>
                    </Card>
                </motion.div>
            </motion.div>
        </>
    );
};

export default Login;
